import { addDays, format, isToday, parseISO } from "date-fns";
import { CalendarClock } from "lucide-react";

import { Card } from "@/components/ui/Card";
import { DAY_LOOK } from "@/components/data/DayMark";
import { LinkButton } from "@/components/ui/LinkButton";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/cn";
import type { MyRotaDay } from "./api";
import { useMyRota } from "./hooks";

/** Shift times read as 08:30, not 8:30 AM. */
function clock(value: string): string {
  return value.slice(0, 5);
}

/** What this day is, in the fewest words that still answer the question. */
function reading(day: MyRotaDay): { line: string; sub: string | null; tone: string } {
  // Leave comes before a shift. Approved leave takes somebody off the rota,
  // but a shift drawn before the leave was approved can still be on the
  // record, and a week that says "08:30–17:00" on a day you are in another
  // country is a week that is lying to you.
  if (day.on_leave) {
    return { line: "Leave", sub: day.on_leave, tone: DAY_LOOK.leave.chip };
  }
  if (day.shift) {
    return {
      line: `${clock(day.shift.start_time)}–${clock(day.shift.end_time)}`,
      sub: day.shift.branch_name,
      tone: DAY_LOOK.shift.chip,
    };
  }
  if (day.public_holiday) {
    return { line: "Holiday", sub: day.public_holiday, tone: DAY_LOOK.holiday.chip };
  }
  if (day.resting) {
    return { line: "Day off", sub: null, tone: DAY_LOOK.rest.chip };
  }
  // Not a rest day and not rostered either: the rota simply has not been
  // written this far ahead yet, which is worth saying rather than dashing.
  return { line: "Not set", sub: "no shift yet", tone: "bg-surface-sunken text-fg-placeholder" };
}

/**
 * The next seven days, at the top of an employee's home screen.
 *
 * This is the question staff open the app to answer, and it used to be four
 * scrolls and a click away behind three leave balances. Seven days from today
 * rather than a calendar week, because "am I in tomorrow" does not care where
 * Monday falls.
 */
export function WeekAhead() {
  const start = new Date();
  const from = format(start, "yyyy-MM-dd");
  const to = format(addDays(start, 6), "yyyy-MM-dd");
  const { data: days, isLoading } = useMyRota(from, to);

  return (
    <Card
      title="Your week"
      description="The next seven days."
      icon={<CalendarClock className="size-4" />}
      actions={
        <LinkButton to="/rota" variant="secondary" size="sm">
          See the month
        </LinkButton>
      }
    >
      {isLoading ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          {Array.from({ length: 7 }).map((_, index) => (
            <Skeleton key={index} className="h-20 w-full" />
          ))}
        </div>
      ) : (
        <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          {(days ?? []).map((day) => {
            const date = parseISO(day.date);
            const today = isToday(date);
            const { line, sub, tone } = reading(day);
            return (
              <li
                key={day.date}
                className={cn(
                  "rounded-sm px-2.5 py-2",
                  tone,
                  today && "ring-2 ring-inset ring-accent",
                )}
              >
                <p className="u-label flex items-baseline gap-1">
                  <span className={cn(today && "font-semibold text-accent")}>
                    {today ? "Today" : format(date, "EEE")}
                  </span>
                  <span className="tabular-nums">{format(date, "d MMM")}</span>
                </p>
                <p className="mt-1 truncate text-sm font-semibold tabular-nums">{line}</p>
                {sub ? <p className="truncate text-2xs opacity-80">{sub}</p> : null}
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}
